import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import axios from 'axios';
import { getAutoPrint, setAutoPrint, printOrder } from '../utils/printOrder';
import './AdminStore.css';

const KIOSK_FLAG = '--kiosk-printing';

// Cupom de exemplo para testar a impressora sem fazer pedido
const TEST_ORDER = {
  id: 0,
  created_at: new Date().toISOString(),
  name: 'TESTE DE IMPRESSÃO',
  phone_number: '(00) 00000-0000',
  delivery_address: 'Rua de Teste, 123',
  delivery_neighborhood: 'Centro',
  notes: 'Se você está lendo isto, a impressora está funcionando!',
  items: [
    { quantity: 2, unit: 'un', product_name: 'Produto de teste', price: 5 },
    { quantity: 0.5, unit: 'kg', product_name: 'Produto por peso', price: 20 }
  ],
  delivery_fee: 2,
  discount: 0,
  total: 22,
  payment_method: 'dinheiro',
  change_for: 50
};

const DAYS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

// 🏪 Loja: aberto/fechado, horários, pedido mínimo, aviso, motoboy padrão e impressora
// 📦 Trazer as fotos do Cloudinary para o nosso banco (o plano grátis do Cloudinary estourou)
function PhotosCard({ authHeaders }) {
  const [info, setInfo] = useState(null);
  const load = () => axios.get(`${API_URL}/images/migrate`, authHeaders()).then(res => setInfo(res.data)).catch(() => setInfo(null));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Enquanto copia, atualiza o andamento a cada 2 segundos
  useEffect(() => {
    if (!info?.running) return undefined;
    const timer = setInterval(load, 2000);
    return () => clearInterval(timer);
  }, [info?.running]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    await axios.post(`${API_URL}/images/migrate`, {}, authHeaders()).catch(() => {});
    load();
  };

  if (!info) return null;
  const mb = (info.stored_bytes / 1024 / 1024).toFixed(1).replace('.', ',');
  return (
    <div className="as-card">
      <h3>📦 Fotos no nosso servidor</h3>
      <p className="as-help">
        As fotos dos produtos, banners, perfis e da aba Imagens ficam guardadas no nosso próprio banco, sem gastar o Cloudinary.
        O Cloudinary fica só para tirar o fundo, quando você marcar.
      </p>
      <p className="as-photos-status">
        ✅ <b>{info.stored}</b> foto(s) aqui ({mb} MB)
        {info.left > 0 && <> · ☁️ <b>{info.left}</b> ainda no Cloudinary</>}
      </p>
      {info.running ? (
        <div className="as-photos-progress">
          <span style={{ width: `${info.total ? ((info.done + info.failed) / info.total) * 100 : 0}%` }} />
          <small>⏳ Copiando {info.done + info.failed} de {info.total}...</small>
        </div>
      ) : info.left > 0 ? (
        <button type="button" className="as-btn-backup" onClick={start}>📦 Trazer as fotos para o nosso servidor</button>
      ) : (
        <p className="as-photos-ok">🎉 Todas as fotos já estão no nosso servidor.</p>
      )}
      {!info.running && info.finished_at && (
        <div className={`as-message ${info.failed ? 'err' : 'ok'}`}>
          {info.failed
            ? `⚠️ ${info.done} copiada(s), ${info.failed} não deu (o Cloudinary pode estar bloqueado). Tente de novo mais tarde.`
            : `✅ ${info.done} foto(s) copiada(s). Os clientes já veem as fotos daqui.`}
        </div>
      )}
    </div>
  );
}

function AdminStore() {
  const [store, setStore] = useState(null);
  const [motoboys, setMotoboys] = useState([]);
  const [autoPrint, setAutoPrintState] = useState(getAutoPrint());
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } });

  useEffect(() => {
    axios.get(`${API_URL}/settings/store/admin`, authHeaders())
      .then(res => setStore(res.data))
      .catch(err => console.error('Erro ao buscar dados da loja:', err));
    axios.get(`${API_URL}/motoboys`, authHeaders())
      .then(res => setMotoboys(res.data.filter(m => m.active)))
      .catch(err => console.error('Erro ao buscar motoboys:', err));
  }, []);

  const showMessage = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(''), 3000);
  };

  const save = async (next = store) => {
    try {
      setSaving(true);
      const res = await axios.put(`${API_URL}/settings/store`, next, authHeaders());
      setStore(res.data);
      showMessage('✅ Salvo!');
    } catch (error) {
      showMessage(`❌ ${error.response?.data?.error || 'Erro ao salvar'}`);
    } finally {
      setSaving(false);
    }
  };

  const setDay = (i, field, value) => {
    setStore({ ...store, hours: store.hours.map((h, idx) => (idx === i ? { ...h, [field]: value } : h)) });
  };

  // 💾 Backup: baixar agora + cópias automáticas guardadas no Cloudinary
  const [backups, setBackups] = useState(null);
  const [backupBusy, setBackupBusy] = useState('');
  const [backupMsg, setBackupMsg] = useState('');

  const loadBackups = () => axios.get(`${API_URL}/backup`, authHeaders())
    .then(res => setBackups(res.data))
    .catch(() => setBackups({ cloud: false, items: [] }));

  useEffect(() => { loadBackups(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadBackup = async (path, busy) => {
    try {
      setBackupBusy(busy);
      setBackupMsg('');
      const res = await axios.get(`${API_URL}/backup/${path}`, { ...authHeaders(), responseType: 'blob' });
      const name = (res.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'backup.json.gz';
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
      setBackupMsg(`✅ Backup baixado: ${name}`);
      loadBackups();
    } catch {
      setBackupMsg('❌ Não consegui baixar o backup');
    } finally {
      setBackupBusy('');
    }
  };

  const saveBackupNow = async () => {
    try {
      setBackupBusy('cloud');
      setBackupMsg('');
      await axios.post(`${API_URL}/backup`, {}, authHeaders());
      await loadBackups();
      setBackupMsg('✅ Cópia guardada');
    } catch (error) {
      setBackupMsg(`❌ ${error.response?.data?.error || 'Erro ao guardar a cópia'}`);
    } finally {
      setBackupBusy('');
    }
  };

  const fmtDate = (iso) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  const daysSince = (iso) => Math.floor((Date.now() - new Date(iso)) / 86400000);
  const backupDays = backups?.downloaded ? daysSince(backups.downloaded) : null;

  // ⚠️ Erros recentes do servidor
  const [errors, setErrors] = useState(null);
  const [openError, setOpenError] = useState(null);
  const loadErrors = () => axios.get(`${API_URL}/errors`, authHeaders())
    .then(res => setErrors(res.data))
    .catch(() => setErrors([]));
  useEffect(() => { loadErrors(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const clearErrors = async () => {
    await axios.delete(`${API_URL}/errors`, authHeaders()).catch(() => {});
    loadErrors();
  };

  const [copied, setCopied] = useState(false);
  const [resetText, setResetText] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState('');

  const resetOrders = async () => {
    try {
      setResetting(true);
      const res = await axios.post(`${API_URL}/settings/reset-orders`, { confirm: resetText }, authHeaders());
      setResetText('');
      setResetResult(`✅ Pedidos zerados (${res.data.deleted} apagados). O próximo pedido será o #1.`);
    } catch (error) {
      setResetResult(`❌ ${error.response?.data?.error || 'Erro ao zerar pedidos'}`);
    } finally {
      setResetting(false);
    }
  };

  const copyFlag = async () => {
    try {
      await navigator.clipboard.writeText(` ${KIOSK_FLAG}`);
    } catch {
      window.prompt('Copie o comando:', ` ${KIOSK_FLAG}`);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const toggleAutoPrint = () => {
    setAutoPrint(!autoPrint);
    setAutoPrintState(!autoPrint);
  };

  if (!store) return <div className="as-loading">Carregando...</div>;

  return (
    <div className="as-container">
      <h2>🏪 Loja</h2>
      {message && <div className={`as-message ${message.includes('✅') ? 'ok' : 'err'}`}>{message}</div>}

      {/* ABERTO / FECHADO */}
      <div className={`as-status ${store.status.open ? 'open' : 'closed'}`}>
        <div>
          <strong>{store.status.open ? '🟢 Loja aberta' : '🔴 Loja fechada'}</strong>
          <span>{store.status.message || 'Recebendo pedidos'}</span>
        </div>
        <button
          onClick={() => save({ ...store, closed_now: !store.closed_now })}
          disabled={saving}
          className={store.closed_now ? 'as-btn-open' : 'as-btn-close'}
        >
          {store.closed_now ? '🔓 Reabrir loja' : '🔒 Fechar agora'}
        </button>
      </div>

      <div className="as-grid">
        {/* PEDIDO MÍNIMO + AVISO */}
        <div className="as-card">
          <h3>💰 Pedido mínimo</h3>
          <p className="as-help">Valor mínimo em produtos (sem a taxa de entrega). Use 0 para não ter mínimo.</p>
          <div className="as-money">
            <span>R$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={store.min_order}
              onChange={(e) => setStore({ ...store, min_order: e.target.value })}
            />
          </div>

          <h3>📢 Aviso na loja</h3>
          <p className="as-help">Aparece no topo para os clientes. Ex: "Frete grátis hoje!" (deixe vazio para não mostrar).</p>
          <input
            type="text"
            maxLength={200}
            className="as-input"
            placeholder="Escreva um aviso..."
            value={store.notice}
            onChange={(e) => setStore({ ...store, notice: e.target.value })}
          />
        </div>

        {/* MOTOBOY PADRÃO + IMPRESSORA */}
        <div className="as-card">
          <h3>🛵 Motoboy padrão</h3>
          <p className="as-help">Todo pedido novo já entra com esse motoboy. Você pode trocar em cada pedido.</p>
          <select
            className="as-input"
            value={store.default_motoboy_id || ''}
            onChange={(e) => setStore({ ...store, default_motoboy_id: e.target.value ? parseInt(e.target.value) : null })}
          >
            <option value="">— Nenhum (escolho em cada pedido) —</option>
            {motoboys.map(m => (
              <option key={m.id} value={m.id}>{m.name} (@{m.username})</option>
            ))}
          </select>

          <h3>🖨️ Impressora</h3>
          <label className="as-switch">
            <input type="checkbox" checked={autoPrint} onChange={toggleAutoPrint} />
            <span>Imprimir pedidos novos automaticamente <em>neste computador</em></span>
          </label>
          <div className="as-print-guide">
            <strong>📋 Como deixar a impressão automática (Bematech MP-4200 TH)</strong>
            <ol>
              <li>Instale o driver da Bematech e deixe a <b>MP-4200 TH como impressora padrão</b> do Windows (papel 80mm).</li>
              <li>
                Crie um atalho do Chrome na área de trabalho. Clique com o botão direito → <b>Propriedades</b> e,
                no fim do campo <b>Destino</b>, acrescente (com um espaço antes):
                <div className="as-flag">
                  <code>{KIOSK_FLAG}</code>
                  <button type="button" className="as-btn-light" onClick={copyFlag}>{copied ? '✅ Copiado' : '📋 Copiar'}</button>
                </div>
              </li>
              <li>Feche <b>todas</b> as janelas do Chrome e abra pelo atalho novo.</li>
              <li>Marque a opção acima (<b>Imprimir pedidos novos automaticamente</b>).</li>
              <li>Deixe o <b>painel Admin aberto</b> durante o expediente: a cada 20 s ele confere pedidos novos, apita e imprime.</li>
            </ol>
            <p className="as-help">
              Manual: <b>📦 Pedidos → 🖨️</b> (primeira coluna). Se aparecer a janela de impressão, o Chrome não foi aberto pelo atalho.
              Se sair em outra impressora, a Bematech não está como padrão.
            </p>
            <button type="button" className="as-btn-light" onClick={() => printOrder({ ...TEST_ORDER, created_at: new Date().toISOString() })}>
              🖨️ Imprimir teste
            </button>
          </div>
        </div>
      </div>

      {/* HORÁRIOS */}
      <div className="as-card">
        <div className="as-card-head">
          <h3>🕒 Horário de funcionamento</h3>
          <label className="as-switch">
            <input
              type="checkbox"
              checked={store.hours_enabled}
              onChange={(e) => setStore({ ...store, hours_enabled: e.target.checked })}
            />
            <span>Usar horário (fora dele, a loja não aceita pedidos)</span>
          </label>
        </div>

        <div className={`as-hours ${store.hours_enabled ? '' : 'disabled'}`}>
          {store.hours.map((h, i) => (
            <div key={i} className="as-day">
              <strong>{DAYS[i]}</strong>
              <label className="as-day-open">
                <input type="checkbox" checked={!h.closed} onChange={(e) => setDay(i, 'closed', !e.target.checked)} />
                {h.closed ? 'Fechado' : 'Aberto'}
              </label>
              <input type="time" value={h.open} disabled={h.closed} onChange={(e) => setDay(i, 'open', e.target.value)} />
              <span>às</span>
              <input type="time" value={h.close} disabled={h.closed} onChange={(e) => setDay(i, 'close', e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <button className="as-save" onClick={() => save()} disabled={saving}>
        {saving ? '⏳ Salvando...' : '💾 Salvar configurações'}
      </button>

      {/* BACKUP */}
      <div className="as-card">
        <h3>💾 Backup</h3>
        <p className="as-help">
          Cópia de <b>tudo</b>: produtos, clientes, pedidos, motoboys, cupons e configurações. As fotos ficam guardadas no
          Cloudinary e não entram no arquivo. Guarde o arquivo no computador, num pen drive ou no Google Drive.
        </p>
        {backups && (
          <p className={`as-backup-age ${backupDays === null || backupDays >= 30 ? 'late' : ''}`}>
            {backupDays === null
              ? '📅 Você ainda não baixou nenhum backup para o computador.'
              : `📅 Último backup baixado ${backupDays === 0 ? 'hoje' : backupDays === 1 ? 'ontem' : `há ${backupDays} dias`}.`}
            {(backupDays === null || backupDays >= 30) && ' Baixe um agora e guarde fora do app (uma vez por mês é o ideal).'}
          </p>
        )}
        <button type="button" className="as-btn-backup" onClick={() => downloadBackup('download', 'now')} disabled={!!backupBusy}>
          {backupBusy === 'now' ? '⏳ Preparando...' : '⬇️ Baixar backup agora'}
        </button>

        {backups?.cloud && (
          <>
            <h3>🔁 Cópias automáticas</h3>
            <p className="as-help">
              Uma por dia, guardadas no Cloudinary (só o admin baixa). Ficam as últimas {backups.keep}.
              {backups.last && <> Última: <b>{fmtDate(backups.last)}</b>.</>}
            </p>
            <ul className="as-backup-list">
              {backups.items.map(b => (
                <li key={b.id}>
                  <span>{fmtDate(b.created_at)} · {Math.max(1, Math.round(b.bytes / 1024))} KB</span>
                  <button type="button" onClick={() => downloadBackup(`cloud/${b.id}`, b.id)} disabled={!!backupBusy}>
                    {backupBusy === b.id ? '⏳' : '⬇️ Baixar'}
                  </button>
                </li>
              ))}
              {backups.items.length === 0 && <li>Nenhuma cópia ainda.</li>}
            </ul>
            <button type="button" className="as-btn-link" onClick={saveBackupNow} disabled={!!backupBusy}>
              {backupBusy === 'cloud' ? '⏳ Guardando...' : '➕ Guardar uma cópia agora'}
            </button>
          </>
        )}
        {backupMsg && <div className={`as-message ${backupMsg.includes('✅') ? 'ok' : 'err'}`}>{backupMsg}</div>}
      </div>

      {/* 📦 FOTOS NO NOSSO SERVIDOR */}
      <PhotosCard authHeaders={authHeaders} />

      {/* ERROS RECENTES */}
      <div className="as-card">
        <h3>⚠️ Erros recentes</h3>
        <p className="as-help">
          Quando algo dá errado no servidor (um pedido que não salvou, uma foto que não subiu), fica anotado aqui.
          Só você vê. Se aparecer algo repetido, me mande o print.
        </p>
        {errors && errors.length === 0 && <p className="as-errors-empty">✅ Nenhum erro. Tudo funcionando.</p>}
        {errors && errors.length > 0 && (
          <>
            <ul className="as-errors">
              {errors.map(e => (
                <li key={e.id}>
                  <button type="button" onClick={() => setOpenError(openError === e.id ? null : e.id)}>
                    <span>{fmtDate(e.created_at)}{e.path && <> · <code>{e.method ? `${e.method} ` : ''}{e.path}</code></>}</span>
                    <strong>{e.message}</strong>
                  </button>
                  {openError === e.id && e.stack && <pre>{e.stack}</pre>}
                </li>
              ))}
            </ul>
            <button type="button" className="as-btn-link" onClick={clearErrors}>🧹 Limpar lista</button>
          </>
        )}
      </div>

      {/* ZERAR PEDIDOS */}
      <div className="as-card as-danger">
        <h3>🧹 Zerar pedidos</h3>
        <p className="as-help">
          Apaga <b>todos os pedidos</b> e zera o Dashboard (vendas, faturamento, mais vendidos). A numeração volta para o #1
          e os usos dos cupons voltam a 0. Clientes, produtos, estoque, motoboys e configurações continuam iguais.
          <b> Não dá para desfazer.</b>
        </p>
        <div className="as-reset-row">
          <input
            type="text"
            value={resetText}
            onChange={(e) => setResetText(e.target.value.toUpperCase())}
            placeholder="Digite ZERAR"
          />
          <button type="button" className="as-btn-danger" onClick={resetOrders} disabled={resetText !== 'ZERAR' || resetting}>
            {resetting ? '⏳ Zerando...' : '🧹 Zerar pedidos'}
          </button>
        </div>
        {resetResult && <div className={`as-message ${resetResult.includes('✅') ? 'ok' : 'err'}`}>{resetResult}</div>}
      </div>
    </div>
  );
}

export default AdminStore;
